import React from 'react';
import * as PIXI from 'pixi.js';
import { PixiComponent, useApp } from '@inlet/react-pixi';
import { Viewport as PixiViewport } from 'pixi-viewport';
import { Point, InteractionEvent } from 'pixi.js';

export interface ViewportProps {
  screenWidth: number;
  screenHeight: number;
  worldWidth: number;
  worldHeight: number;
  onMoveEnd?: (centre: Point) => void;
  onZoom: (scaleX: number, scaleY: number) => void;
  children?: React.ReactNode;
  onClick?: (e: InteractionEvent) => void;
}

export interface PixiComponentViewportProps extends ViewportProps {
  app: PIXI.Application;
}

const getZoomScaleByScreenWidth = (screenWidth: number) => ((screenWidth < 1199) ? 15 : 5);

const PixiComponentViewport = PixiComponent('Viewport', {
  create: ({
    screenWidth, screenHeight, worldWidth, worldHeight,
    app: {
      ticker, renderer: { plugins: { interaction } },
    },
    onZoom,
    onMoveEnd,
    onClick,
  }: PixiComponentViewportProps) => {
    const viewport = new PixiViewport({
      screenWidth,
      screenHeight,
      worldWidth,
      worldHeight,
      ticker,
      interaction,
    });
    viewport.drag().pinch().wheel().decelerate()
      /**
       * We stop the user from zooming in too close. On mobile however we must allow for a bigger
       * zoom factor as screenWidth is much smaller than the worldWidth
       */
      .clampZoom({ minScale: 1, maxScale: getZoomScaleByScreenWidth(screenWidth) })
      // Restrict viewport to fit the island
      .clamp({ direction: 'all' });

    viewport.on('zoomed', (data) => {
      const { _x: scaleX, _y: scaleY } = data.viewport.scale;

      onZoom(scaleX, scaleY);
    });

    viewport.on('click', (event) => {
      onClick?.(event);
    });

    viewport.on('moved-end', (data) => {
      if (onMoveEnd) {
        onMoveEnd(data.center);
      }
    });

    return viewport;
  },
});

const Viewport = (props: ViewportProps): React.ReactElement => {
  const app = useApp();

  // eslint-disable-next-line react/jsx-props-no-spreading
  return (<PixiComponentViewport app={app} {...props} />);
};

export default Viewport;
